/**
 * SharedSettingsContext - A common interface for settings.
 *
 * This allows hooks like useDateTimeFormat to work across the app.
 */
import { createContext, useContext, ReactNode } from 'react';
import { Settings } from '@ais-ui/websockets';

/**
 * Minimal settings interface
 */
export interface SharedSettingsContextType {
  settings: Settings;
  dataUpdatedAt: number | null;
}

export const SharedSettingsContext =
  createContext<SharedSettingsContextType | null>(null);

/**
 * Hook to access shared settings
 */
export const useSharedSettings = (): SharedSettingsContextType => {
  const context = useContext(SharedSettingsContext);
  if (!context) {
    throw new Error('useSharedSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SharedSettingsProviderProps {
  children: ReactNode;
  settings: Settings;
  dataUpdatedAt: number | null;
}

/**
 * Wrapper component that provides the shared settings context
 * Used internally by both SettingsProvider and PopupSettingsProvider
 */
export const SharedSettingsProvider = ({
  children,
  settings,
  dataUpdatedAt,
}: SharedSettingsProviderProps) => {
  return (
    <SharedSettingsContext.Provider value={{ settings, dataUpdatedAt }}>
      {children}
    </SharedSettingsContext.Provider>
  );
};
